import React, { useContext, useRef, useState } from 'react'
import { Button, ListMenu, ListMenuButton, Modal, Popover,
   Spacer, TabMenu, TabMenuButton, View } from 'oio-react'
import { Route, useHistory, useParams, useRouteMatch } from 'react-router-dom'
import { MoreIcon } from 'assets/icons'
import { settingsPeopleUrl, settingsPeopleRoleUrl, settingsSiteReportsUrl }
   from 'config/constants/urls'
import ContentListSearchBox from 'src/components/Settings/ContentListSearchBox'
import ProtoHeadingBlock from 'src/components/Settings/ProtoHeadingBlock'
import ProtoReturnBar from 'src/components/Settings/ProtoReturnBar'
import RoleImport from 'src/components/Settings/RoleImport'
import RoleInvite from 'src/components/Settings/RoleInvite'
import RoleUserList from 'src/components/Settings/RoleUserList'
import { useCreateReport, useOrganization } from 'src/graphql/hooks'
import ThemeContext from 'src/libraries/contexts/Theme'

const SettingsPeopleRole = () => {
   const popoverButton = useRef()
   const [popoverIsOpen, setPopoverIsOpen] = useState(false)
   const [modalIsOpen, setModalIsOpen] = useState(true)
   const [searchBoxValue, setSearchBoxValue] = useState('')
   const [activeSearchValue, setActiveSearchValue] = useState('')
   const theme = useContext(ThemeContext)

   const history = useHistory()
   const match = useRouteMatch()
   const { roleId, roleStatus } = useParams()

   const { createReport } = useCreateReport()
   const { organization } = useOrganization()
   const role = organization.roles.find(r => r.id === roleId)

   const links = [{
      name: 'Active',
      badgeNumber: role.numActiveUsers,
      linkTo: `${settingsPeopleRoleUrl}/${roleId}`,
      linkExact: true
   }, {
      name: 'Invited',
      badgeNumber: role.numInvitedUsers,
      linkTo: `${settingsPeopleRoleUrl}/${roleId}/invited`
   }]

   if (role.numInactiveUsers) {
      links.push({
         name: 'Inactive',
         badgeNumber: role.numInactiveUsers || 0,
         linkTo: `${settingsPeopleRoleUrl}/${roleId}/inactive`
      })
   }

   if (role.numDeclinedUsers) {
      links.push({
         name: 'Declined',
         badgeNumber: role.numDeclinedUsers,
         linkTo: `${settingsPeopleRoleUrl}/${roleId}/declined`
      })
   }

   const handleExportToCsv = () => {
      // Note: we don't wait for the report generation to finish,
      // otherwise this would just hang for a while, especially
      // if the reports are large. We redirect to reports listing
      // right away.
      // TODO: Better error handling, though the ReportList should
      // show if report generation crapped out.
      createReport({
         roleId,
         type: 'OrganizationPeople'
      })

      history.push(settingsSiteReportsUrl)
   }

   const handleInvitationSuccess = () => {
      setModalIsOpen(false)
      setSearchBoxValue('')
      setActiveSearchValue('')
   }

   return (
      <View>
         <ProtoReturnBar
            display="block[a-d] none[e-f]"
            returnLinkName="People, Privacy & Roles"
            returnLinkTo={settingsPeopleUrl}
         />
         <View
            display="flex"
            justifyContent="center">
            <View
               width="100%"
               maxWidth="100%[a-c] 600px[d-f]"
               padding="40px 20px">
               <View
                  width="100%"
                  marginBottom="24px"
                  display="flex"
                  flexFlow="column[a-c] row[d-f]">
                  <View paddingRight="60px">
                     <ProtoHeadingBlock
                        title={role.name}
                        description={`A list of all users belonging to the Community ${role.name} role. You can invite new users to this role here.`}
                        metatext={`There are ${role.numActiveUsers} active users in this role`}
                     />
                  </View>
                  <View
                     flex="0 0 auto"
                     paddingTop="30px">
                     <View
                        display="flex"
                        alignItems="center">
                        <Button
                           linkTo={`${settingsPeopleRoleUrl}/${roleId}/invited/new`}
                           width="90px"
                           name="Invite"
                           color={theme.protoSettingsHighlightBackgroundColor}
                           textColor={theme.protoSettingsHighlightForegroundColor}
                           textWeight="medium"
                           size="md"
                           textSize="0.9"
                           padding="25px"
                        />
                        <Spacer size="1" orientation="vertical" />
                        <Button
                           ref={popoverButton}
                           onClick={() => setPopoverIsOpen(true)}
                           width="30px"
                           color="#ddd"
                           textColor="#111"
                           textWeight="bold"
                           size="md">
                           <MoreIcon width="18px" height="18px" />
                        </Button>
                        <Popover
                           anchorElement={popoverButton.current}
                           anchorOriginHorizontal="right"
                           anchorOriginVertical="top"
                           borderRadius="6px"
                           width="150px"
                           onBodyClick={() => setPopoverIsOpen(false)}
                           open={popoverIsOpen}>
                           <View
                              float="left"
                              width="100%"
                              borderRadius="6px"
                              backgroundColor="#fff"
                              boxShadow="6px 6px 30px rgba(0,0,0,0.1)">
                              <ListMenu buttonSize="md">
                                 <ListMenuButton
                                    linkTo={`${settingsPeopleRoleUrl}/${roleId}/import`}
                                    name="Import from .csv"
                                 />
                                 <ListMenuButton
                                    onClick={handleExportToCsv}
                                    name="Export to .csv"
                                 />
                              </ListMenu>
                           </View>
                        </Popover>
                     </View>
                  </View>
               </View>
               <TabMenu
                  backgroundColor="#F7F7F4"
                  buttonPaddingHorizontal="0px"
                  buttonSpacing="25px">
                  {links.map(link => (
                     <TabMenuButton
                        key={link.name}
                        name={link.name}
                        badgeNumber={link.badgeNumber}
                        linkTo={link.linkTo}
                        linkExact={link.linkExact}
                     />
                  ))}
               </TabMenu>
               <View width="100%">
                  <ContentListSearchBox
                     value={searchBoxValue}
                     onChange={setSearchBoxValue}
                     onSubmit={setActiveSearchValue}
                  />
                  <RoleUserList
                     roleId={roleId}
                     searchValue={activeSearchValue || null}
                     status={roleStatus}
                     targetType="organization"
                  />
               </View>
            </View>
         </View>
         <Route
            path={`${settingsPeopleRoleUrl}/:roleId/import/:batchImportId?`}
            render={() => (
               <Modal
                  borderRadius="9px"
                  width="100%[a-b] 90%[c] 900px[d-f]"
                  height="100%[a-b] 90%[c] 80%[d-f]"
                  onCloseComplete={() => {
                     history.push(match.url)
                     setModalIsOpen(true)
                  }}
                  onCloseTrigger={() => setModalIsOpen(false)}
                  open={modalIsOpen}>
                  <RoleImport
                     baseUrl={`${settingsPeopleRoleUrl}/${roleId}/import`}
                     onCloseButtonClick={() => setModalIsOpen(false)}
                     targetType="organization"
                  />
               </Modal>
            )}
         />
         <Route
            path={`${settingsPeopleRoleUrl}/${roleId}/invited/new`}
            render={() => (
               <Modal
                  borderRadius="6px"
                  width="100%[a-b] 330px[c-f]"
                  onCloseComplete={() => {
                     history.push(match.url)
                     setModalIsOpen(true)
                  }}
                  onCloseTrigger={() => setModalIsOpen(false)}
                  open={modalIsOpen}>
                  <RoleInvite
                     onCloseButtonClick={() => setModalIsOpen(false)}
                     onInvite={handleInvitationSuccess}
                     roleId={role.id}
                     roleName={role.name}
                     targetType="organization"
                  />
               </Modal>
            )}
         />
      </View>
   )
}

export default SettingsPeopleRole
